import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError } from "@/lib/api";

export default function Signup() {
  const { signup } = useCustomerAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signup({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
      });
      navigate("/account", { replace: true });
    } catch (err) {
      setError(
        formatApiError(err?.response?.data?.detail) || "Signup failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--st-cream)] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="block font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-fg)]/60 hover:text-[var(--st-maroon)] mb-6"
          data-testid="account-signup-home-link"
        >
          ← Substitute Teacher
        </Link>
        <div className="bg-white border-2 border-[#111] shadow-brutal p-8">
          <h1 className="font-heading uppercase text-3xl text-[var(--st-fg)] mb-1">
            Create account
          </h1>
          <p className="text-sm text-[var(--st-fg)]/70 mb-6">
            Faster checkout, your orders all in one place.
          </p>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                  First name
                </label>
                <input
                  value={form.first_name}
                  onChange={onChange("first_name")}
                  required
                  autoComplete="given-name"
                  className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                  data-testid="account-signup-first-name"
                />
              </div>
              <div>
                <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                  Last name
                </label>
                <input
                  value={form.last_name}
                  onChange={onChange("last_name")}
                  required
                  autoComplete="family-name"
                  className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                  data-testid="account-signup-last-name"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                required
                autoComplete="email"
                className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                data-testid="account-signup-email"
              />
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
                Password{" "}
                <span className="text-[var(--st-fg)]/40 normal-case">
                  (min 8 characters)
                </span>
              </label>
              <input
                type="password"
                value={form.password}
                onChange={onChange("password")}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
                data-testid="account-signup-password"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-700 bg-red-100 border-2 border-red-700 px-3 py-2"
                data-testid="account-signup-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="account-signup-submit"
              className="w-full font-display uppercase tracking-tight text-base py-3 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] transition-colors shadow-brutal-sm disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create account"}
            </button>
          </form>
          <div className="mt-6 text-sm text-[var(--st-fg)]/70">
            Already have an account?{" "}
            <Link
              to="/account/login"
              className="text-[var(--st-maroon)] underline"
              data-testid="account-signup-to-login"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
